import { apiClient } from '@/lib/api/client'

/**
 * SOLY-TEAM-001 — unified team roster API client.
 *
 * Endpoints exposed by TeamAdminController:
 *   GET    /api/admin/team                       — one row per person (members + classic pending invitations)
 *   POST   /api/admin/team                       — create a team member without account
 *   PUT    /api/admin/team/{memberId}            — edit name / functions / email (while no access)
 *   DELETE /api/admin/team/{memberId}/access     — revoke access (person stays in team + planning)
 *   POST   /api/admin/team/{memberId}/deactivate — soft team exit (history keeps the name)
 *   POST   /api/admin/team/{memberId}/reactivate — back to the pre-deactivation state
 *   DELETE /api/admin/team/{memberId}            — physical delete (sheets created by mistake only)
 *
 * Deferred invitations go through POST /invitations with a userId (see admin.ts).
 */

export type TeamFunction = 'Commercial' | 'Surveyor' | 'Installer'

export type TeamAccessStatus = 'deactivated' | 'invited' | 'active' | 'none'

export type TeamRoleDto = {
  roleId: string
  label: string
}

export type TeamMemberDto = {
  /** Null for a classic pending invitation (person not created yet). */
  userId: string | null
  /** Set when an invitation is pending for this row (revoke/resend target). */
  invitationId: string | null
  displayName: string
  /** Raw identity fields — initial values for the edit dialog. */
  firstName: string | null
  lastName: string | null
  email: string | null
  functions: TeamFunction[]
  /** Access role — null when the person has no access. */
  role: TeamRoleDto | null
  accessStatus: TeamAccessStatus
  /** False when soft-deactivated (hidden from active screens). */
  isActive: boolean
  deactivatedAt: string | null
}

export type TeamResponse = {
  organizationId: string
  members: TeamMemberDto[]
}

export type CreateTeamMemberRequest = {
  firstName: string
  lastName: string
  email?: string | null
  functions: TeamFunction[]
}

export type UpdateTeamMemberRequest = {
  firstName: string
  lastName: string
  /** Null/omitted = unchanged. Only editable while the member has no access. */
  email?: string | null
  functions: TeamFunction[]
}

export async function getTeam(): Promise<TeamResponse> {
  return apiClient.get<TeamResponse>('/api/admin/team')
}

export async function createTeamMember(request: CreateTeamMemberRequest): Promise<TeamMemberDto> {
  return apiClient.post<TeamMemberDto>('/api/admin/team', request)
}

export async function updateTeamMember(
  memberId: string,
  request: UpdateTeamMemberRequest,
): Promise<void> {
  await apiClient.put<void>(`/api/admin/team/${memberId}`, request)
}

export async function revokeTeamMemberAccess(memberId: string): Promise<void> {
  await apiClient.delete<void>(`/api/admin/team/${memberId}/access`)
}

export async function deactivateTeamMember(memberId: string): Promise<void> {
  await apiClient.post<void>(`/api/admin/team/${memberId}/deactivate`)
}

export async function reactivateTeamMember(memberId: string): Promise<void> {
  await apiClient.post<void>(`/api/admin/team/${memberId}/reactivate`)
}

export async function deleteTeamMember(memberId: string): Promise<void> {
  await apiClient.delete<void>(`/api/admin/team/${memberId}`)
}
